import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Database,
  FileSpreadsheet,
  FileWarning,
  Layers,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FileUploader } from '@/components/import/file-uploader';
import { parseSalesCSV, type SalesCsvParseResult } from '@/lib/sales-csv-parser';
import { cn } from '@/lib/utils';
import {
  createReport,
  listMonitors,
  TEMPLATE_LABELS,
  ALL_BRAND,
  ALL_BRAND_LABEL,
} from '@/lib/api';
import type {
  AvailablePeriods,
  BrandStat,
  ReportTemplateType,
  SalesImportRow,
} from '@/lib/api';

type Step = 'source' | 'csv-detect' | 'db-form' | 'metadata';
type Source = 'csv' | 'db';
type PeriodType = 'month' | 'quarter' | 'year' | 'range';

const PERIOD_TYPE_LABELS: Record<PeriodType, string> = {
  month: 'Tháng',
  quarter: 'Quý',
  year: 'Năm',
  range: 'Range',
};

interface CsvBrandSummary {
  brand: string;
  skuCount: number;
  rowCount: number;
}

interface PeriodResolved {
  start: string;
  end: string;
  label: string;
}

function formatMonthLabel(month: string): string {
  const [y, m] = month.split('-');
  return `T${parseInt(m!, 10)}/${y!.slice(2)}`;
}

function formatQuarterLabel(q: string): string {
  const [y, n] = q.split('-Q');
  return `Q${n} ${y}`;
}

function quarterToRange(q: string): { start: string; end: string } | null {
  const m = q.match(/^(\d{4})-Q([1-4])$/);
  if (!m) return null;
  const year = m[1]!;
  const num = parseInt(m[2]!, 10);
  const startMonth = (num - 1) * 3 + 1;
  return {
    start: `${year}-${String(startMonth).padStart(2, '0')}`,
    end: `${year}-${String(startMonth + 2).padStart(2, '0')}`,
  };
}

function suggestTitle(brand: string, periodLabel: string): string {
  if (!brand || !periodLabel) return '';
  const brandPart = brand === ALL_BRAND ? 'Monitor Category' : brand;
  return `${periodLabel} ${brandPart} Performance Review`;
}

function periodLabelFromRange(start: string, end: string): string {
  if (start === end) return formatMonthLabel(start);
  // Detect quarter pattern (same year, exact 3-month span aligned to Q boundaries)
  const [ys, ms] = start.split('-');
  const [ye, me] = end.split('-');
  if (ys === ye) {
    const sm = parseInt(ms!, 10);
    const em = parseInt(me!, 10);
    const q = Math.ceil(sm / 3);
    if (sm === (q - 1) * 3 + 1 && em === q * 3) {
      return `Q${q} ${ys}`;
    }
  }
  return `${ms}/${ys} → ${me}/${ye}`;
}

/** Group CSV rows by brand using id→brand map. Returns sorted brands with SKU + row counts. */
function summarizeCsvByBrand(
  rows: SalesImportRow[],
  idToBrand: Map<string, string>
): CsvBrandSummary[] {
  const buckets = new Map<string, { skus: Set<string>; rowCount: number }>();
  for (const r of rows) {
    const brand = idToBrand.get(r.monitor_id);
    if (!brand) continue; // unknown SKU → skip in detection (still UPSERT-ed by backend)
    const cur = buckets.get(brand) ?? { skus: new Set(), rowCount: 0 };
    cur.skus.add(r.monitor_id);
    cur.rowCount += 1;
    buckets.set(brand, cur);
  }
  return Array.from(buckets.entries())
    .map(([brand, b]) => ({ brand, skuCount: b.skus.size, rowCount: b.rowCount }))
    .sort((a, b) => b.rowCount - a.rowCount);
}

function detectPeriodFromRows(rows: SalesImportRow[]): PeriodResolved | null {
  if (rows.length === 0) return null;
  const months = rows.map((r) => r.month).filter(Boolean).sort();
  const start = months[0];
  const end = months[months.length - 1];
  if (!start || !end) return null;
  return { start, end, label: periodLabelFromRange(start, end) };
}

export interface ReportCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brands: BrandStat[];
  available: AvailablePeriods | null;
  onCreated: () => void;
}

export function ReportCreateModal({ open, onOpenChange, brands, available, onCreated }: ReportCreateModalProps) {
  // Wizard step
  const [step, setStep] = useState<Step>('source');
  const [source, setSource] = useState<Source | null>(null);

  // CSV path state
  const [csvParseResult, setCsvParseResult] = useState<SalesCsvParseResult | null>(null);
  const [csvBrands, setCsvBrands] = useState<CsvBrandSummary[]>([]);
  const [csvPeriod, setCsvPeriod] = useState<PeriodResolved | null>(null);
  const [parsing, setParsing] = useState(false);
  const [monitorIdMap, setMonitorIdMap] = useState<Map<string, string> | null>(null);

  // DB path state
  const [dbBrand, setDbBrand] = useState('');
  const [dbPeriodType, setDbPeriodType] = useState<PeriodType>('quarter');
  const [dbMonthValue, setDbMonthValue] = useState('');
  const [dbQuarterValue, setDbQuarterValue] = useState('');
  const [dbYearValue, setDbYearValue] = useState('');
  const [dbRangeStart, setDbRangeStart] = useState('');
  const [dbRangeEnd, setDbRangeEnd] = useState('');

  // Final selection (carries through to step 3)
  const [selectedBrand, setSelectedBrand] = useState('');
  const [resolvedPeriod, setResolvedPeriod] = useState<PeriodResolved | null>(null);

  // Metadata (step 3)
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [templateType, setTemplateType] = useState<ReportTemplateType>('partner');
  const [introText, setIntroText] = useState('');

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset wizard whenever modal closes
  useEffect(() => {
    if (!open) {
      setStep('source');
      setSource(null);
      setCsvParseResult(null);
      setCsvBrands([]);
      setCsvPeriod(null);
      setParsing(false);
      setDbBrand('');
      setDbPeriodType('quarter');
      setDbMonthValue('');
      setDbQuarterValue('');
      setDbYearValue('');
      setDbRangeStart('');
      setDbRangeEnd('');
      setSelectedBrand('');
      setResolvedPeriod(null);
      setTitle('');
      setTitleTouched(false);
      setTemplateType('partner');
      setIntroText('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  // Auto-suggest title when reaching step 3, until user edits it manually
  useEffect(() => {
    if (titleTouched) return;
    setTitle(suggestTitle(selectedBrand, resolvedPeriod?.label ?? ''));
  }, [selectedBrand, resolvedPeriod?.label, titleTouched]);

  // ---- Step 1 → CSV path ----
  const handleFileSelected = async (file: File) => {
    setError(null);
    setCsvParseResult(null);
    setCsvBrands([]);
    setCsvPeriod(null);
    setParsing(true);
    try {
      const result = await parseSalesCSV(file);
      setCsvParseResult(result);

      if (result.errors.length > 0 || result.rows.length === 0) {
        // Stay on source step — user sees error, can re-upload
        return;
      }

      // Lazily fetch monitor id→brand map (first CSV only). Cache in state for subsequent re-uploads.
      // Backend caps limit at 100/page so we paginate; GearVN currently <100 SKUs → 1 call,
      // but the loop guards against future catalog growth.
      let idMap = monitorIdMap;
      if (!idMap) {
        try {
          const collected = new Map<string, string>();
          let page = 1;
          while (true) {
            const { data, meta } = await listMonitors({ limit: 100, page });
            for (const m of data) collected.set(m.id, m.brand);
            if (page >= meta.pages || data.length === 0) break;
            page += 1;
            if (page > 50) break; // safety guard — > 5000 SKUs is unrealistic
          }
          idMap = collected;
          setMonitorIdMap(idMap);
        } catch (err) {
          setError(`Lỗi tải danh sách SKU: ${(err as Error).message}`);
          return;
        }
      }

      const brandSummaries = summarizeCsvByBrand(result.rows, idMap);
      const period = detectPeriodFromRows(result.rows);

      if (brandSummaries.length === 0) {
        setError('CSV không match SKU nào trong DB. Kiểm tra cột monitor_id.');
        return;
      }
      if (!period) {
        setError('CSV không có cột month hợp lệ.');
        return;
      }

      setCsvBrands(brandSummaries);
      setCsvPeriod(period);
      setSource('csv');
      setStep('csv-detect');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setParsing(false);
    }
  };

  // ---- Step 1 → DB path ----
  const handlePickDbSource = () => {
    setSource('db');
    setStep('db-form');
  };

  // ---- Step 2a CSV continue ----
  const csvContinue = () => {
    if (!selectedBrand || !csvPeriod) {
      setError('Vui lòng chọn 1 hãng để tạo báo cáo');
      return;
    }
    setError(null);
    setResolvedPeriod(csvPeriod);
    setStep('metadata');
  };

  // ---- Step 2b DB continue ----
  // Distinct years available from `sales_monthly` — derive from months list.
  const availableYears = useMemo(() => {
    const set = new Set<string>();
    (available?.months ?? []).forEach((m) => {
      const [y] = m.split('-');
      if (y) set.add(y);
    });
    return Array.from(set).sort();
  }, [available]);

  const dbResolvedPeriod = useMemo<PeriodResolved | null>(() => {
    if (dbPeriodType === 'month' && dbMonthValue) {
      return { start: dbMonthValue, end: dbMonthValue, label: formatMonthLabel(dbMonthValue) };
    }
    if (dbPeriodType === 'quarter' && dbQuarterValue) {
      const r = quarterToRange(dbQuarterValue);
      if (!r) return null;
      return { start: r.start, end: r.end, label: formatQuarterLabel(dbQuarterValue) };
    }
    if (dbPeriodType === 'year' && dbYearValue) {
      return { start: `${dbYearValue}-01`, end: `${dbYearValue}-12`, label: `Năm ${dbYearValue}` };
    }
    if (dbPeriodType === 'range' && dbRangeStart && dbRangeEnd) {
      if (dbRangeStart > dbRangeEnd) return null;
      return {
        start: dbRangeStart,
        end: dbRangeEnd,
        label: periodLabelFromRange(dbRangeStart, dbRangeEnd),
      };
    }
    return null;
  }, [dbPeriodType, dbMonthValue, dbQuarterValue, dbYearValue, dbRangeStart, dbRangeEnd]);

  const dbContinue = () => {
    if (!dbBrand) {
      setError('Vui lòng chọn hãng');
      return;
    }
    if (!dbResolvedPeriod) {
      setError('Vui lòng chọn period hợp lệ');
      return;
    }
    setError(null);
    setSelectedBrand(dbBrand);
    setResolvedPeriod(dbResolvedPeriod);
    setStep('metadata');
  };

  // ---- Step 3 submit ----
  const handleSubmit = async () => {
    setError(null);
    if (!selectedBrand || !resolvedPeriod) {
      setError('Thiếu thông tin brand/period — quay lại bước trước');
      return;
    }
    if (!title.trim()) {
      setError('Vui lòng nhập tiêu đề');
      return;
    }

    const salesRows: SalesImportRow[] | undefined =
      source === 'csv' && csvParseResult && csvParseResult.rows.length > 0
        ? csvParseResult.rows
        : undefined;

    setSubmitting(true);
    try {
      await createReport({
        brand: selectedBrand,
        period_start: resolvedPeriod.start,
        period_end: resolvedPeriod.end,
        title: title.trim(),
        // Status defaults to 'draft' on backend; user can change later via Edit modal.
        // Description omitted at create — added later via Edit if needed.
        template_type: templateType,
        intro_text: introText.trim() || undefined,
        sales_rows: salesRows,
      });
      onCreated();
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Header per-step ----
  const stepInfo = useMemo(() => {
    switch (step) {
      case 'source':
        return { title: 'Tạo báo cáo mới', sub: 'Chọn nguồn data: upload CSV mới hoặc dùng data đã có trong DB.' };
      case 'csv-detect':
        return { title: 'Chọn hãng từ CSV', sub: 'Đã parse CSV — chọn 1 hãng để tạo báo cáo, hoặc gộp Toàn ngành.' };
      case 'db-form':
        return { title: 'Chọn hãng + period', sub: 'Tạo báo cáo từ data đã có trong DB — không cần upload thêm CSV.' };
      case 'metadata':
        return { title: 'Tiêu đề + cấu hình', sub: 'Chọn loại báo cáo và (tùy chọn) lời mở đầu. Snapshot sẽ frozen tại thời điểm tạo.' };
    }
  }, [step]);

  const csvHasErrors = csvParseResult && csvParseResult.errors.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{stepInfo.title}</DialogTitle>
          <DialogDescription>{stepInfo.sub}</DialogDescription>
          <StepIndicator step={step} source={source} />
        </DialogHeader>

        {/* ============ STEP 1: source picker ============ */}
        {step === 'source' && (
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              {/* Card 1 — CSV upload */}
              <button
                type="button"
                onClick={() => setSource('csv')}
                className={cn(
                  'flex flex-col items-start gap-2 rounded-lg border bg-card p-4 text-left transition-colors',
                  'hover:border-primary/50 hover:bg-primary/5',
                  source === 'csv' && 'border-primary bg-primary/5'
                )}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <FileSpreadsheet className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold">Upload CSV mới</div>
                  <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                    Hệ thống auto-detect brand + period từ data CSV. UPSERT vào DB trước khi compute snapshot.
                  </p>
                </div>
                <span className="mt-1 text-[10px] font-medium uppercase tracking-wider text-primary">
                  Recommended
                </span>
              </button>

              {/* Card 2 — DB existing */}
              <button
                type="button"
                onClick={handlePickDbSource}
                className="flex flex-col items-start gap-2 rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-primary/5"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Database className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold">Dùng data có sẵn</div>
                  <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                    Manual chọn brand + period — data sales đã có trong DB từ trước.
                  </p>
                </div>
              </button>
            </div>

            {source === 'csv' && (
              <div className="space-y-2 border-t pt-3">
                <FileUploader onFileSelected={handleFileSelected} disabled={parsing} />
                {parsing && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Đang parse CSV + detect brand...
                  </div>
                )}
                {csvHasErrors && (
                  <Alert variant="destructive">
                    <FileWarning className="h-4 w-4" />
                    <AlertTitle>CSV có {csvParseResult.errors.length} lỗi</AlertTitle>
                    <AlertDescription className="text-xs">
                      <ul className="ml-4 list-disc">
                        {csvParseResult.errors.slice(0, 3).map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============ STEP 2a: CSV detect ============ */}
        {step === 'csv-detect' && csvParseResult && csvPeriod && (
          <div className="space-y-3">
            <Alert variant="success">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>
                Đã parse {csvParseResult.rows.length} dòng · {csvBrands.length} hãng · period {csvPeriod.label}
              </AlertTitle>
              <AlertDescription className="text-xs">
                Period auto-detected:{' '}
                <span className="font-mono">{csvPeriod.start}</span> →{' '}
                <span className="font-mono">{csvPeriod.end}</span>
              </AlertDescription>
            </Alert>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Tạo báo cáo cho <span className="text-destructive">*</span>
              </label>
              <div className="space-y-1.5">
                {csvBrands.map((b) => (
                  <BrandRadio
                    key={b.brand}
                    value={b.brand}
                    selected={selectedBrand === b.brand}
                    onSelect={() => setSelectedBrand(b.brand)}
                    label={b.brand}
                    detail={`${b.skuCount} SKU · ${b.rowCount} sales rows`}
                  />
                ))}
                {csvBrands.length > 1 && (
                  <BrandRadio
                    value={ALL_BRAND}
                    selected={selectedBrand === ALL_BRAND}
                    onSelect={() => setSelectedBrand(ALL_BRAND)}
                    label={ALL_BRAND_LABEL}
                    detail={`gộp ${csvBrands.length} hãng · ${csvParseResult.rows.length} rows · cho sếp/họp nội bộ`}
                    icon={<Layers className="h-4 w-4 text-primary" />}
                    accent
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============ STEP 2b: DB form ============ */}
        {step === 'db-form' && (
          <div className="space-y-4">
            {/* Brand */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Hãng <span className="text-destructive">*</span>
              </label>
              <Select value={dbBrand} onValueChange={setDbBrand}>
                <SelectTrigger>
                  <SelectValue placeholder="Chọn hãng..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_BRAND}>
                    <span className="flex items-center gap-1.5">
                      <Layers className="h-3.5 w-3.5 text-primary" />
                      <span className="font-medium">{ALL_BRAND_LABEL}</span>
                      <span className="text-muted-foreground">— gộp tất cả hãng</span>
                    </span>
                  </SelectItem>
                  {brands.map((b) => (
                    <SelectItem key={b.brand} value={b.brand}>
                      {b.brand} <span className="ml-1 text-muted-foreground">({b.count} SKU)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Period */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Period <span className="text-destructive">*</span>
              </label>
              <div className="flex gap-1.5">
                {(['month', 'quarter', 'year', 'range'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setDbPeriodType(t)}
                    className={cn(
                      'flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                      dbPeriodType === t
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background text-muted-foreground hover:bg-accent'
                    )}
                  >
                    {PERIOD_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>

              {dbPeriodType === 'month' && (
                <Select value={dbMonthValue} onValueChange={setDbMonthValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Chọn tháng..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(available?.months ?? []).map((m) => (
                      <SelectItem key={m} value={m}>{formatMonthLabel(m)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {dbPeriodType === 'quarter' && (
                <Select value={dbQuarterValue} onValueChange={setDbQuarterValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Chọn quý..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(available?.quarters ?? []).map((q) => (
                      <SelectItem key={q} value={q}>{formatQuarterLabel(q)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {dbPeriodType === 'year' && (
                <Select value={dbYearValue} onValueChange={setDbYearValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Chọn năm..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.map((y) => (
                      <SelectItem key={y} value={y}>Năm {y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {dbPeriodType === 'range' && (
                <div className="grid grid-cols-2 gap-2">
                  <Select value={dbRangeStart} onValueChange={setDbRangeStart}>
                    <SelectTrigger><SelectValue placeholder="Từ tháng..." /></SelectTrigger>
                    <SelectContent>
                      {(available?.months ?? []).map((m) => (
                        <SelectItem key={m} value={m}>{formatMonthLabel(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={dbRangeEnd} onValueChange={setDbRangeEnd}>
                    <SelectTrigger><SelectValue placeholder="Đến tháng..." /></SelectTrigger>
                    <SelectContent>
                      {(available?.months ?? []).map((m) => (
                        <SelectItem key={m} value={m}>{formatMonthLabel(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {dbResolvedPeriod && (
                <p className="text-[11px] text-muted-foreground">
                  Range: <span className="font-mono">{dbResolvedPeriod.start}</span> →{' '}
                  <span className="font-mono">{dbResolvedPeriod.end}</span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* ============ STEP 3: metadata ============ */}
        {step === 'metadata' && resolvedPeriod && (
          <div className="space-y-4">
            {/* Summary card — locked from prev steps */}
            <div className="rounded-md bg-muted/40 p-3 text-xs">
              <div className="flex items-center gap-2">
                {selectedBrand === ALL_BRAND && <Layers className="h-3.5 w-3.5 text-primary" />}
                <div>
                  <span className="font-semibold">
                    {selectedBrand === ALL_BRAND ? ALL_BRAND_LABEL : selectedBrand}
                  </span>
                  <span className="text-muted-foreground"> · {resolvedPeriod.label}</span>
                  {source === 'csv' && csvParseResult && (
                    <span className="text-muted-foreground">
                      {' '}· CSV {csvParseResult.rows.length} rows
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="report-title" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Tiêu đề <span className="text-destructive">*</span>
              </label>
              <Input
                id="report-title"
                value={title}
                onChange={(e) => {
                  setTitleTouched(true);
                  setTitle(e.target.value);
                }}
                placeholder="Auto-suggest từ brand × period"
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Loại báo cáo <span className="text-destructive">*</span>
              </label>
              <Select value={templateType} onValueChange={(v) => setTemplateType(v as ReportTemplateType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TEMPLATE_LABELS) as ReportTemplateType[]).map((key) => {
                    const t = TEMPLATE_LABELS[key];
                    const Icon = t.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-1.5">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-medium">{t.label}</span>
                          <span className="text-muted-foreground">— {t.description}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Quyết định section nào hiển thị (donut, SKU table…). Đổi sau qua Edit.
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="report-intro" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Lời mở đầu (tùy chọn)
                <span className="ml-1 font-normal text-muted-foreground">— max 500 ký tự</span>
              </label>
              <Textarea
                id="report-intro"
                value={introText}
                onChange={(e) => setIntroText(e.target.value)}
                placeholder="vd: Quý này hãng push QD-OLED, sales tăng 32% nhờ campaign Black Friday..."
                maxLength={500}
                className="min-h-[72px]"
              />
              <p className="text-[11px] text-muted-foreground">
                Hiển thị trên đầu báo cáo. Plain text — line breaks giữ nguyên. Description chi tiết bổ sung sau qua Edit.
              </p>
            </div>
          </div>
        )}

        {/* ============ Error banner (all steps) ============ */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* ============ Footer per step ============ */}
        <DialogFooter>
          {step === 'source' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Hủy
            </Button>
          )}

          {step === 'csv-detect' && (
            <>
              <Button variant="outline" onClick={() => { setStep('source'); setSource(null); }}>
                <ArrowLeft className="h-4 w-4" />
                Đổi nguồn
              </Button>
              <Button onClick={csvContinue} disabled={!selectedBrand}>
                Tiếp tục
              </Button>
            </>
          )}

          {step === 'db-form' && (
            <>
              <Button variant="outline" onClick={() => { setStep('source'); setSource(null); }}>
                <ArrowLeft className="h-4 w-4" />
                Đổi nguồn
              </Button>
              <Button onClick={dbContinue} disabled={!dbBrand || !dbResolvedPeriod}>
                Tiếp tục
              </Button>
            </>
          )}

          {step === 'metadata' && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep(source === 'csv' ? 'csv-detect' : 'db-form')}
                disabled={submitting}
              >
                <ArrowLeft className="h-4 w-4" />
                Quay lại
              </Button>
              <Button onClick={handleSubmit} disabled={!title.trim() || submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Tạo báo cáo
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Visual breadcrumb showing current wizard step. Skipped on small screens to save space. */
function StepIndicator({ step, source }: { step: Step; source: Source | null }) {
  const isCsv = source === 'csv';
  const items: Array<{ key: Step | 'select'; label: string }> = [
    { key: 'source', label: '1. Nguồn' },
    { key: 'select', label: isCsv ? '2. Chọn hãng' : '2. Brand + period' },
    { key: 'metadata', label: '3. Metadata' },
  ];

  // Map current step to indicator index
  const currentIdx = step === 'source' ? 0 : step === 'metadata' ? 2 : 1;

  return (
    <nav className="hidden items-center gap-1 pt-2 text-[11px] sm:flex" aria-label="Wizard progress">
      {items.map((item, i) => (
        <div key={item.key} className="flex items-center gap-1">
          <span
            className={cn(
              'rounded-full px-2 py-0.5 font-medium transition-colors',
              i === currentIdx
                ? 'bg-primary/10 text-primary'
                : i < currentIdx
                  ? 'text-foreground'
                  : 'text-muted-foreground'
            )}
          >
            {item.label}
          </span>
          {i < items.length - 1 && <span className="text-muted-foreground/40">›</span>}
        </div>
      ))}
    </nav>
  );
}

/** Radio-style brand picker for CSV detect step. */
function BrandRadio({
  value,
  selected,
  onSelect,
  label,
  detail,
  icon,
  accent,
}: {
  value: string;
  selected: boolean;
  onSelect: () => void;
  label: string;
  detail: string;
  icon?: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-left transition-colors',
        selected ? 'border-primary bg-primary/5' : 'hover:border-primary/40 hover:bg-muted/30',
        accent && !selected && 'border-primary/20 bg-primary/[0.03]'
      )}
      aria-pressed={selected}
    >
      <div
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
          selected ? 'border-primary' : 'border-border'
        )}
      >
        {selected && <div className="h-2 w-2 rounded-full bg-primary" />}
      </div>
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-[11px] text-muted-foreground">{detail}</div>
      </div>
      {/* Hidden value for a11y */}
      <input type="radio" value={value} checked={selected} readOnly className="sr-only" tabIndex={-1} />
    </button>
  );
}
